/* global */
/**
 * Takes care of validating forms and initializes the address checker
 * module if address input fields are present.
 *
 * @author Meinaart van Straalen
 * @author Tim van Oostrom
 * @author Vincent Bruijn <vincent-bruijn@g-star.com>
 *
 * // will prevent the default action
 * <form data-ajax-submit="true"></form>
 *
 * // will allow you to handle formSubmits from another module
 * $('form').on(EventTypes.FORM_VALIDATED, function(e,formIsValid){ if (formIsValid) {} })
 *
 * // gives you access to this module
 * var formApi = $('form').data('api')
 *
 * FormControls will only be attached to forms having data attribute data-controlled
 * regardless the value of the attribute. This is a whitelist approach, and replaces
 * the former blacklist approach.
 *
 * (!) If you want to have a form controlled by FormControls, you need to add
 * data-controlled to the form's tag.
 *
 */
import $ from 'jquery';
import createLogger from './logger/Logger';
import Factory from './utils/Factory';
import InputRestriction from './inputRestriction/InputRestriction';
import AnalyticsEventTypes from './analytics/AnalyticsEventTypes';
import EventTypes from './EventTypes';
import BirthdateValidation from './birthdateValidation';
import PostcodeValidation from './PostcodeValidation';
import InputMonth from './inputMonth';
import Coordinator from './Coordinator';
import scrollTo from './utils/scrollTo';
import defineLazyJquerySelector from './utils/defineLazyJquerySelector';
import 'jquery.pageshowEvents';
import 'jquery.extendPrototype';
import 'jquery.serializeObject';
import './domutils/Element.jQuery';
import GenderValidation from './genderValidation';
import EmailValidation from './emailValidation';
import requestIdleCallback from './utils/requestIdleCallback';
import AccountNewsletterPreferences from '../components/AccountNewsletterPreferences';

const Logger = createLogger('FormControls');

const dateOfBirth = 'dateOfBirth';

// these inputs have their own validation included
const ignoredInputTypes = ['email'];

function FormControls($form) {
  if ($form.get(0).closest('[id*="react"]')) {
    return;
  }
  Logger.measure(this, ['deferredInitialize', 'initialize', 'radioAndCheckboxControl']);
  this.initialize($form);
}

/**
 * Validate identicality of password and passwordCheck field values
 * @param  {string} second Value of passwordCheck field
 * @param  {string} first  Value of password field
 * @return {boolean}       indenticality of arguments
 */
function validatePasswordCheck(second, first) {
  return first.length > 0 && first === second;
}

$.extendPrototype(FormControls, {
  form: undefined,
  lastErrorFieldName: false,
  errorTrackingEnabled: false,
  submitting: false,
  eventNamespace: '.formControls',

  /**
   * Initialize module
   */
  initialize($form) {
    this.form = $form.get(0);
    this.form.setAttribute('novalidate', '');
    this.form.jq.data('api', this);
    this.form.captchaContainer = $form.find('#recaptcha-container').get(0);
    this.form.captchaEnabled = !!this.form.captchaContainer;

    // Add this.$submitButton as lazy jQuery object
    defineLazyJquerySelector(
      this,
      '$submitButton',
      'button[type="submit"],input[type="submit"]',
      this.form.jq
    );

    this.setupConditionalRequiredFields();

    this.bindEvents();

    Object.defineProperty(this, 'allowSubmit', {
      configurable: true,
      get() {
        return this.__allowSubmit !== false;
      },
      set(value) {
        Logger.info('set allowSubmit:', value);
        this.__allowSubmit = value;
        this.submitting = false;
      },
    });

    const idleCallback = () => {
      this.deferredInitialize();
    };

    requestIdleCallback(idleCallback);
  },

  /**
   * Replace {0} in a string to a value coming from an object
   */
  messageTemplate(label, data) {
    return label.replace(/\{[0-9]+\}/g, m0 => {
      return data[m0] || '';
    });
  },

  /**
   * Stuff that can be initialized later on
   */
  deferredInitialize() {
    const birthdateSelector = `[name^=${dateOfBirth}]`;
    if (this.form.querySelector(birthdateSelector)) {
      new BirthdateValidation(this.form, birthdateSelector);
    }

    const emailInputSelector = '[type=email]';
    if (this.form.querySelector(emailInputSelector)) {
      new EmailValidation(this.form, emailInputSelector);
    }

    const newslettersSelector = '.account-newsletters';
    if (this.form.closest(newslettersSelector)) {
      new AccountNewsletterPreferences();
    }

    const genderInputSelector = '[name$=gender], [name=preferredCategory]';
    if (this.form.querySelectorAll(genderInputSelector).length) {
      GenderValidation.initialize(this.form, genderInputSelector);
    }

    const postcodeSelector = '[name$=postcode]';
    if (this.form.querySelector(postcodeSelector)) {
      PostcodeValidation.initialize($(postcodeSelector));
    }

    if (
      this.form.querySelectorAll(
        '[name$="street"],[name$="city"],[name$="houseNumber"],[name$="postcode"]'
      ).length > 2
    ) {
      const billingAddressClass = '.js-billingAddress, .js-fieldset-address';
      const deliveryAddressClass = '.js-deliveryAddress';
      const shippingAddressClass = '.js-checkbox-shippingAddress';
      if (document.querySelector(shippingAddressClass)) {
        import(
          /* webpackChunkName: "import--CreateShippingAddress" */ './CreateShippingAddress'
        ).then(_ => {
          const CreateShippingAddress = _.default;
          new CreateShippingAddress({
            element: shippingAddressClass,
            billingAddress: billingAddressClass,
            deliveryAddress: deliveryAddressClass,
          });
        });
      } else {
        import(
          /* webpackChunkName: "import--AddressChecker" */ './addressChecker/AddressChecker'
        ).then(_ => {
          const AddressChecker = _.default;
          new AddressChecker({
            element: billingAddressClass,
            prefix: '',
          });
        });
      }

      if (this.form.querySelectorAll('[name="latitude"],[name="longitude"]').length) {
        Coordinator.initialize(this.form);
      }
    }

    this.radioAndCheckboxControl();
    this.removeInvalidFromNotRequiredFields();
    this.form.captchaEnabled && this.initializeCaptcha();

    if (this.hasErrors()) {
      if (this.form.getAttribute('data-disable-scrollto') === 'false') {
        this.scrollToError();
      } else {
        scrollTo(0);
      }
    }

    InputMonth.initialize();

    this.handleFormValidity(this.form.getElementsByClassName('invalid').length === 0, false, true);

    const formContainer =
      document.querySelector('section.account') ??
      document.querySelector('#checkoutAccount-loginForm.loginForm');
    const formInputGroups =
      formContainer?.querySelectorAll('.formInputGroup:not(.formInputGroup--checkbox)') ?? [];

    formInputGroups.forEach(formInputGroup => {
      if (
        formInputGroup.classList.contains('formInputGroup--checkbox') ||
        formInputGroup.classList.contains('formInputGroup--preferredStore') ||
        formInputGroup.classList.contains('formInputGroup--radio')
      )
        return;

      const inputElement = formInputGroup.querySelector('input');

      if (!inputElement || inputElement.name === 'preferredStore') return;
      if (inputElement.type === 'password') {
        const toggle = formInputGroup.querySelector('.gstar-icon.icon-eye');

        if (toggle) {
          toggle.onclick = () => {
            if (inputElement.type === 'password') {
              inputElement.type = 'text';
              toggle.classList.remove('icon-eye');
              toggle.classList.add('icon-eye-slash');
            } else {
              inputElement.type = 'password';
              toggle.classList.remove('icon-eye-slash');
              toggle.classList.add('icon-eye');
            }
          };
        }
      }

      if (inputElement.value || inputElement.autofocus) {
        formInputGroup.classList.add('minimized');
      }

      inputElement.onchange = ({ target }) => {
        if (target.value) {
          formInputGroup.classList.add('minimized');
        } else {
          formInputGroup.classList.remove('minimized');
        }
      };

      inputElement.oninput = ({ target }) => {
        if (target.value) {
          formInputGroup.classList.add('minimized');
        } else {
          formInputGroup.classList.remove('minimized');
        }
      };

      inputElement.onfocus = () => {
        formInputGroup.classList.add('minimized');
      };

      inputElement.onblur = ({ target }) => {
        if (target.value) return;

        formInputGroup.classList.remove('minimized');
      };
    });

    const selectInputGroups = document.querySelectorAll('section.account select');

    selectInputGroups.forEach(select => {
      const label = select.parentNode.querySelector('.formInputLabel');

      if (select.value) {
        label.classList.add('minimized');
      }

      select.onchange = ({ target }) => {
        if (target.value) {
          label.classList.add('minimized');
        } else {
          label.classList.remove('minimized');
        }
      };

      select.onfocus = () => {
        label.classList.add('minimized');
      };

      select.onblur = ({ target }) => {
        if (target.value) return;

        label.classList.remove('minimized');
      };
    });

    document.jq.trigger(EventTypes.DEFERRED_INITIALIZE_COMPLETE);
  },

  /**
   * Bind event to form fields needed for validation
   */
  bindEvents() {
    document.jq
      .on(EventTypes.FORM_ELEMENT_ERROR_HIDE, this.hideError.bind(this))
      .on(EventTypes.FORM_ELEMENT_ERROR_SHOW, this.showError.bind(this));

    const contextSelector =
      'input:not([type=checkbox]):not([type=radio]):not([readonly]), select, textarea';
    this.form.jq
      .on(
        'focusout.formControls validateInput.formControls',
        contextSelector,
        this.validateInputField.bind(this)
      )
      .on(
        'change.formControls validateInput.formControls',
        '[type=checkbox], [type=radio]',
        this.validateInputField.bind(this)
      )
      .on(`submit${this.eventNamespace}`, this.onSubmitForm.bind(this));

    if (this.conditionals && this.conditionals.length) {
      let conditionalObj;
      for (let i = 0, leni = this.conditionals.length; i < leni; i++) {
        conditionalObj = this.conditionals[i];
        this.form.jq.on(
          'input',
          conditionalObj.fieldIds.join(','),
          conditionalObj,
          this.onConditionalInputChange.bind(this)
        );
      }
    }

    // reenable submit button if iOS shows page from cache
    window.jq.on(EventTypes.PAGESHOW_FROM_CACHE, this.enableSubmitButton.bind(this));
  },

  /**
   * Check if there are fields that need correction
   * @param  {Object} addressData Object containing address
   */
  checkAddressFieldsThatNeedInputCorrection(addressData) {
    const { form } = this;
    let noCorrectionRequired = true;

    $.each(addressData, (name, value) => {
      const isValueValid = InputRestriction.isValueRestrictedByName(name, value);
      const $input = form.jq.find(`[name="${name}"]`);

      $input.data('invalid', false).nextAll('.formInputError--inputRestriction').remove();

      if (!isValueValid && $input.val() && $input.is('input')) {
        $input.data({
          invalid: true,
        });

        $input
          .closest('label')
          .after(
            `<p class="error formInputError--inputRestriction"></span>${window.labels.forms.messageCheckInput}</p>`
          )
          .closest('.formInputGroup, label')
          .addClass('invalid')
          .removeClass('valid');
        noCorrectionRequired = false;
      }
    });

    return noCorrectionRequired;
  },

  /**
   * Disable form button
   */
  disableSubmitButton(stateLabel) {
    const submitButtons = this.form.querySelectorAll('[type=submit]');

    submitButtons.forEach(submitButton => {
      const label = submitButton.dataset.prevLabel;

      if (label) {
        submitButton.innerHTML = label;
      }

      this.submitting = false;
      submitButton.disabled = true;
      submitButton.classList.remove('loading');
    });
  },

  /**
   * Disable form button and add 'Loading...'label
   */
  loadingSubmitButton(stateLabel) {
    const submitButtons = this.form.querySelectorAll('[type=submit]');
    submitButtons.forEach(submitButton => {
      const label = stateLabel || submitButton.dataset.loadingLabel || 'Loading...';
      const prevLabel = submitButton.dataset.prevLabel || submitButton.innerHTML;
      this.submitting = true;
      submitButton.disabled = true;
      submitButton.innerHTML = label;
      submitButton.setAttribute('data-prev-label', prevLabel);
      submitButton.classList.add('loading');
    });
  },

  /**
   * Enable submit button and put back original label
   */
  enableSubmitButton() {
    const submitButtons = this.form.querySelectorAll('[type=submit]');
    submitButtons.forEach(submitButton => {
      const label = submitButton.dataset.prevLabel;
      if (label) {
        submitButton.innerHTML = label;
      }
      this.submitting = false;
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
    });
  },

  /**
   * Invoke the reCAPTCHA challenge and in case of success submit form using callback
   */
  handleSubmitWithCaptcha() {
    if (this.form.captchaResponseToken) {
      window.grecaptcha.reset();
    }

    this.form.jq.find('#g-recaptcha-response').attr('name', 'captcha');

    window.grecaptcha.execute();
  },

  /**
   * Get errors
   * @return {jQuery} jQuery object containing DOM elements which contain error messages
   */
  getErrors() {
    return this.form.jq.find('.formInputError,.invalid');
  },

  /**
   * Get CSRFToken
   */
  getFormCsrfToken() {
    if (!this.formCsrfToken) {
      this.formCsrfToken = this.form.jq.find('input[name="CSRFToken"]').val();
    }
    return this.formCsrfToken;
  },

  /**
   * Does form have errors
   * @return {Boolean} True if it does
   */
  hasErrors() {
    return this.form.querySelectorAll('.formInputError, .invalid').length > 0;
  },

  /**
   * Set valid or invalid style and remove error message if valid
   * @param  {Object}  input       DOM element
   * @param  {Object}  label       DOM element
   * @param  {Object}  formInputGroup  DOM element
   * @param  {Boolean} valid       If input valid or not
   */
  handleAppearance(input, valid) {
    const formInputGroup = input.closest('.formInputGroup');
    const label = input.closest('label');
    if (label) {
      label.classList.toggle('invalid', !valid);
      label.classList.toggle('valid', valid);
    }

    if (!formInputGroup) {
      return;
    }

    if (formInputGroup.classList.contains('js-formInputGroup--checkbox-pseudo-radio')) {
      const checkboxes = formInputGroup.querySelectorAll('[type=checkbox]');
      checkboxes.forEach(checkbox => {
        if (checkbox !== input) {
          checkbox.checked = false;
        }
      });
    } else if (
      formInputGroup &&
      (!formInputGroup.classList.contains('js-birthdateSelect') ||
        !formInputGroup.classList.contains('js-formInputGroup--multiFields'))
    ) {
      formInputGroup.classList.toggle('invalid', !valid);
      formInputGroup.classList.toggle('valid', valid);
    }

    const formInputError = formInputGroup.querySelector('.formInputError');
    if (formInputError && valid) {
      document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_HIDE, [this.form, input]);
    }
  },

  /**
   * Initialize and render google reCAPTCHA
   */
  initializeCaptcha() {
    if (this.form.captchaInitialized) {
      return;
    }

    const siteKey = this.form.captchaContainer?.dataset.sitekey;
    const captcha = window.grecaptcha;

    if (captcha && captcha.render) {
      captcha.render('recaptcha-container', {
        sitekey: `${siteKey}`,
        callback: this.submitForm.bind(this, undefined, true),
        size: 'invisible',
        badge: 'inline',
      });

      this.form.captchaInitialized = !!this.form.captchaContainer?.childNodes.length;
      this.form.capchaError = this.form.captchaEnabled && !this.form.captchaInitialized;
    }
  },

  /**
   * Event listener for form submit event, this makes sure that handling is executed after other handlers.
   * @param  {jQuery.Event} event
   */
  onSubmitForm(event) {
    this.loadingSubmitButton();

    // Make sure it's executed last
    event.preventDefault();

    this.clearValidations();

    if (this.hasErrors()) {
      this.enableSubmitButton();
    }

    if (this.form.captchaInitialized) {
      this.handleSubmitWithCaptcha();
      return false;
    }

    setTimeout(this.submitForm.bind(this, event, true), 0);

    return false;
  },

  /**
   * Remove error message
   * @param  {Object} $element    jQuery object in which to look for errors
   */
  removeErrorMessage(element) {
    const inputError = element.querySelectorAll(
      '.formInputError, .formInputError--inputRestriction'
    );
    inputError.forEach(input => (input.innerHTML = ''));
  },

  /**
   * Remove invalid from fields that don't have a pattern or are not required when something changes
   */
  removeInvalidFromNotRequiredFields() {
    const elements = this.form.querySelectorAll(
      '.invalid input:not([required]):not([pattern]):not([type="checkbox"]):not([type="radio"])'
    );
    if (!elements.length) {
      return;
    }
    $(elements).one(
      ['change', 'keydown', 'textInput', ''].join('.removeInvalidFromNotRequiredFields '),
      event => {
        $(event.currentTarget).off('.removeInvalidFromNotRequiredFields').trigger('validateInput');
      }
    );
  },

  /**
   * Scroll to first field which has errors
   */
  scrollToError() {
    if (!this.form.jq.data('scrollToErrorsDisabled') && this.hasErrors()) {
      const { top } = this.getErrors().eq(0).offset();
      scrollTo(top - 225);
    }
  },

  /**
   * Submit form when there are no errors
   * @param  {jQuery.event} event
   */
  submitForm(event, force, token) {
    this.form.captchaResponseToken = token;

    if (!this.submitting || force) {
      Logger.debug('submitForm');
      Logger.time('submitForm', this);

      // `clearValidations()` removes all existing errors and re-checks all fields besides `birthdateSelect`
      if (this.form.querySelectorAll('.js-birthdateSelect .invalid').length) {
        this.enableSubmitButton();

        return false;
      }

      const $termCheckbox = this.form.jq.find('.js-formInputAcceptTerms :checkbox[required]');
      let valid =
        $termCheckbox.length > 0
          ? $termCheckbox.toArray().some(el => {
              return $(el).prop('checked');
            })
          : true;

      this.checkAddressFieldsThatNeedInputCorrection(this.form.jq.serializeObject());
      this.errorTrackingEnabled = false;

      if (this.hasErrors()) {
        if (this.form.getAttribute('data-disable-scrollto') === 'false') {
          this.scrollToError();
        } else {
          scrollTo(0);
        }
        valid = false;
      }

      if (!this.allowSubmit) {
        valid = false;
      }

      this.form.jq.data('valid', valid);

      if (!valid) {
        this.enableSubmitButton();
      }

      this.handleFormValidity(valid, true);

      Logger.debug('submitForm, allowed:', this.allowSubmit, 'valid:', valid);
      Logger.timeEnd('submitForm', this);

      if (this.form.capchaError && valid && window.labels?.genericError) {
        document.jq.trigger(EventTypes.ALERT_RENDER_REQUEST, [
          'danger',
          window.labels?.genericError,
        ]);

        scrollTo(0);
        this.enableSubmitButton();
        return;
      }

      const shouldAjaxSubmit = this.form.jq.data('ajaxSubmit');
      if (shouldAjaxSubmit === true) {
        Logger.debug('Not submitting form, handled in other component via Ajax');

        this.processPreSubmitHook(this.triggerPreSubmitHooksDone.bind(this, valid));
        return false;
      }

      const eventIsDefaultPrevented = event ? event.isDefaultPrevented() : !!token;

      if (valid && eventIsDefaultPrevented && !shouldAjaxSubmit) {
        Logger.debug('Submitting valid form');

        this.processPreSubmitHook(() => {
          if (valid && !this.hasErrors()) {
            document.jq.trigger(
              AnalyticsEventTypes.NEWSLETTER_SUBSCRIBE,
              this.form.jq.serializeObject()
            );

            this.triggerPreSubmitHooksDone(true);

            this.form.jq.unbind(this.eventNamespace).submit();
          } else {
            this.enableSubmitButton();
            return false;
          }
        });
      }
    } else {
      Logger.debug('submitForm prevented: already submitting.');
    }
  },

  processPreSubmitHook(submitCallback) {
    const preSubmitCallbacks = this.getSortedSubmitCallbacks();

    const failCallback = () => {
      this.enableSubmitButton();
    };

    if (preSubmitCallbacks.length) {
      Logger.debug(`About to processs ${preSubmitCallbacks.length} async preSubmitCallbacks`);
      let deferred = $.when(preSubmitCallbacks.shift().call());

      preSubmitCallbacks.forEach(callback => {
        deferred = deferred.then(callback);
      });

      deferred.fail(failCallback);
      deferred.done(submitCallback);
    } else {
      submitCallback.call();
    }
  },

  triggerPreSubmitHooksDone(isFormValid) {
    this.form.jq.trigger(EventTypes.PRE_SUBMIT_HOOKS_DONE, isFormValid);
  },

  attachSubmitCallback(callbackFunction, priority, uniqId) {
    if (typeof callbackFunction !== 'function') {
      throw new Error('submitCallback should be a function.');
    }
    let preSubmitHandlers = this.form.jq.data('preSubmitHandlers') || [];

    const callbackData = [callbackFunction, priority, uniqId];

    preSubmitHandlers.push(callbackData);
    preSubmitHandlers = preSubmitHandlers.sort((a, b) => (a[1] > b[1] ? 1 : a[1] < b[1] ? -1 : 0));
    this.form.jq.data('preSubmitHandlers', preSubmitHandlers);
  },

  detachSubmitCallback(uniqId) {
    let preSubmitHandlers = this.form.jq.data('preSubmitHandlers');

    preSubmitHandlers = preSubmitHandlers.filter(element => {
      return element[2] !== uniqId;
    });

    this.form.jq.data('preSubmitHandlers', preSubmitHandlers);
  },

  getSortedSubmitCallbacks() {
    const preSubmitHandlers = this.form.jq.data('preSubmitHandlers') || [];
    if (preSubmitHandlers.length === 0) {
      return [];
    }

    return preSubmitHandlers.map(element => element[0]);
  },

  /**
   * Trigger form validation
   * @return {Boolean} Does this form has errors
   */
  clearValidations() {
    this.form.jq
      .find(
        '.validation-message,.error-info,.formInputError:not(.error--email):not(.error--j_username)'
      )
      .remove();
    this.form.jq.find('.box-notice.error').remove();
    this.form.jq
      .find(
        '.invalid:not(.formInputGroup--email > label):not(.error--j_username):not(.formInputGroup--newsletterSubscription),.error:not(.error--email):not(.error--j_username)'
      )
      .removeClass('invalid error');
    this.form.jq.find('.js-error-message').hide();

    let selector =
      'label:visible select[required],label:visible select.required,input[required]:visible,';
    selector += 'textarea[required]:visible,input.required:visible,textarea.required:visible,';
    selector += 'input[required].js-forceValidation,select[required].js-forceValidation';
    const $required = this.form.jq.find(selector);
    $required.trigger('validateInput', true);

    return this.hasErrors();
  },

  /**
   * Update form valid status
   * @param  {Boolean} formValid             Is form valid
   * @param  {Boolean} triggeredByFormSubmit Triggered by submit?
   * @param  {Boolean} triggeredOnFormLoad   Triggered on form load?
   */
  handleFormValidity(formValid, triggeredByFormSubmit, triggeredOnFormLoad) {
    this.form.classList.toggle('is-invalid', !formValid);
    this.form.classList.toggle('is-valid', formValid);

    if (triggeredByFormSubmit) {
      this.form.jq.trigger(EventTypes.FORM_VALIDATED, formValid);
    }
    if (triggeredByFormSubmit || triggeredOnFormLoad) {
      if (window.labels?.registerGenericError && !formValid) {
        document.jq.trigger(EventTypes.ALERT_RENDER_REQUEST, [
          'danger',
          window.labels.registerGenericError,
        ]);
      }
    }
  },

  /**
   * Initialize radio buttons and checkboxes
   */
  radioAndCheckboxControl() {
    const $input = this.form.jq.find(
      '.formInputGroup--radio input,' +
        '.formInputGroup--checkbox input,' +
        '.formInputGroup--checkboxMembership input,' +
        '.js-formInputGroup--checkbox input'
    );

    if ($input.length) {
      $input
        .filter('[type="radio"]')
        .off('vclick.check')
        .on('vclick.check', event => {
          const input = event.currentTarget || this;
          input.jq.trigger('input');
        });

      $input
        .filter('[type="checkbox"]')
        .off('vclick.check')
        .on('vclick.check', event => {
          if (
            !(event.srcElement && event.srcElement.tagName === 'A') &&
            !(event.target && event.target.tagName === 'A')
          ) {
            const _this = this;
            const { checked } = _this;

            if (checked && _this.hasAttribute('required')) {
              const inputGroup = _this.closest('.formInputGroup');
              if (inputGroup) {
                inputGroup.classList.remove('invalid');
              }
            }
          }
        });
    }

    const radioGroup = this.form.jq.find('.js-formInputGroup--checkbox-pseudo-radio');
    radioGroup
      .find('[type="checkbox"]')
      .off('vclick.check')
      .on('vclick.check', event => {
        const $input = $(event.currentTarget || this);
        const isThisChecked = $input[0].checked;

        const checked = radioGroup.find('[type="checkbox"]:checked');

        if (checked.length === 2) {
          checked.prop('checked', false);
        }
        if (!isThisChecked) {
          $input.prop('checked', false);
        } else {
          $input.prop('checked', true);
        }
      });
  },

  /**
   * Check for conditional required fields,
   * Cache selectors and push to conditionals object
   * @return {[type]}
   */
  setupConditionalRequiredFields() {
    const conditionalFields = document.querySelectorAll('input[data-conditional-require]');
    if (conditionalFields.length) {
      this.conditionals = [];
      let conditionalObj;
      conditionalFields.forEach(elem => {
        conditionalObj = { el: elem.jq };
        conditionalObj.fieldIds = conditionalObj.el.attr('data-conditional-require').split(',');
        conditionalObj.$fields = this.getElementForSelectors(conditionalObj.fieldIds);
        this.conditionals.push(conditionalObj);
      });
    }
  },

  /**
   * Helper function to create selectors from string
   * @param  {Array} selectors       Array of selectors strings
   * @return {Array} $selectors      Array of cached jqury objects
   */
  getElementForSelectors(selectors) {
    return $.map(selectors, s => {
      return $(s);
    });
  },

  /**
   * Helper function that checks if required fields are empty
   * @param  {Array} fields   array with jquery input fields
   * @return {Boolean}
   */
  isFieldsEmpty(fields) {
    for (let i = 0, leni = fields.length; i < leni; i++) {
      if (fields[i].val().length > 0) {
        return true;
      }
    }
    return false;
  },

  /**
   * Input change handler for input fields that have conditional requirements
   * @param {Object} e     event;
   */
  onConditionalInputChange(e) {
    if (this.isFieldsEmpty(e.data.$fields)) {
      e.data.el.attr('required', 'required');
    } else {
      e.data.el.removeAttr('required');
    }
  },

  /**
   * Validate input field
   * @param  {jQuery.Event} event
   * @param  {Boolean} triggeredByFormSubmit True if validation is triggered by form submit
   */
  validateInputField(event, triggeredByFormSubmit) {
    const input = event.currentTarget;
    const isRequired = !!input.hasAttribute('required');

    if (ignoredInputTypes.indexOf(input.type) > -1 || input.name.indexOf(dateOfBirth) > -1) {
      // special case email empty and required
      if (!(input.type.toLowerCase() === 'email' && input.value.trim() === '' && isRequired)) {
        return;
      }
    }

    let inputValue = input.value;
    let passwordConstraint;
    const selects = '.page-doormanRegisterPage select[id*=Birth]';
    const selectsHaveValues =
      document.querySelector(selects) &&
      Array.from(document.querySelectorAll(selects)).filter((a, b) => a.value !== '').length !== 0;

    if (input.tagName.toLowerCase() === 'select' && input.selectedIndex !== -1) {
      inputValue = input.options[input.selectedIndex].value;
      selectsHaveValues
        ? $(selects).attr('required', 'required')
        : $(selects).removeAttr('required');
    } else if (input.type.toLowerCase() === 'radio') {
      inputValue = input.checked
        ? input.value
        : this.form.jq.find(`[name="${this.name}"]:checked`).length > 0;
    } else if (input.type.toLowerCase() === 'checkbox') {
      inputValue = input.checked ? input.value : undefined;
    }

    inputValue = inputValue ? inputValue.trim() : inputValue;

    // skip validation if user focuses out and didn't input anything
    if (event.type === 'focusout' && !inputValue && !triggeredByFormSubmit) {
      return false;
    }

    let valid = (inputValue && isRequired) || !isRequired;

    if (input.checkValidity) {
      valid = input.validity.valid;
    }

    if ($.inArray(input.name, ['repeatPassword', 'checkPwd']) > -1) {
      valid = validatePasswordCheck(
        inputValue,
        this.form.jq
          .find(
            'input[type="password"][name="password"],' +
              'input[type="password"][name="pwd"],' +
              'input[type="text"][name="password"],' +
              'input[type="text"][name="pwd"]'
          ) // eslint-disable-line
          .eq(0)
          .val()
      );
    }

    const minLength = input.getAttribute('minlength');
    if (minLength) {
      valid = valid && inputValue.length >= minLength;
    }

    if ($.inArray(input.name, ['password', 'pwd']) > -1 && minLength) {
      // check if first 5 characters are numbers
      passwordConstraint = new RegExp(`^[0-9]{${minLength},}`);

      if (inputValue.length < minLength && passwordConstraint.test(inputValue)) {
        valid = false;
      }
    }

    if (!valid) {
      const formId = this.form.id === 'changePasswordForm' ? 'changePasswordForm' : 'addressForm';
      const inputId = /^_?password$/i.test(input.id) ? 'password' : input.id;
      const errorMessage =
        window.labels?.forms?.[formId]?.[`${inputId}Invalid`] ||
        this.messageTemplate(window.labels?.forms?.messageRequiredInput, {
          '{0}': input?.labels[0]?.innerText?.toLowerCase().split('*')[0],
        });

      document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_SHOW, [this.form, input, errorMessage]);
    }

    this.handleAppearance(input, valid);

    if (!triggeredByFormSubmit) {
      this.handleFormValidity(this.form.getElementsByClassName('invalid').length === 0);
    }
  },

  destroy() {
    Factory.destroyInstance(this, this.form);
  },

  hideError(_, form, element) {
    const target = form.querySelector(`[name=${element.name}]`);
    const formInputGroup = target.closest('.formInputGroup');
    const formInputError = formInputGroup.querySelector('.formInputError');

    if (formInputGroup.classList.contains('birthdateSelect')) {
      if (formInputError) {
        formInputError.parentNode.removeChild(formInputError);
      }

      formInputGroup.classList.add('invalid');
      formInputGroup.classList.remove('valid');
      return;
    }

    if (formInputGroup.classList.contains('formInputGroup--email')) {
      const label = formInputGroup.querySelector('label');
      if (label) {
        label.classList.remove('invalid');
      }
    }

    if (formInputError) {
      formInputGroup.removeChild(formInputError);
    }

    formInputGroup.classList.add('valid');
    formInputGroup.classList.remove('invalid');
  },

  showError(_, form, element, message) {
    const target = form.querySelector(`[name=${element.name}]`);
    const formInputGroup = target.closest('.formInputGroup');
    const formInputError = formInputGroup.querySelector('.formInputError');
    const prevErrorDescription = form.querySelector(`[id=errorDescription__${element.name}]`);
    const hideError = !!target.hasAttribute('hide-error');

    if (
      (formInputError &&
      formInputError.classList.contains(`error--${element.name}`) &&
      prevErrorDescription.innerHTML === message) ||
      hideError
    ) {
      return;
    }
    if (formInputError) {
      document.jq.trigger(EventTypes.FORM_ELEMENT_ERROR_HIDE, [this.form, target]);
    }

    if (!target.hasAttribute('aria-describedby')) {
      target.setAttribute('aria-describedby', `errorDescription__${element.name}`);
    }

    formInputGroup.classList.add('invalid');
    formInputGroup.classList.remove('valid');

    const error = document.createElement('p');
    error.dataset.csCapture = '';
    error.classList.add('formInputError');
    error.classList.add('error');
    error.classList.add(`error--${element.name}`);
    error.id = target.getAttribute('aria-describedby');
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'assertive');
    error.innerHTML = message;

    formInputGroup.appendChild(error);

    document.jq.trigger(AnalyticsEventTypes.ARTIFICIAL_PAGE_VIEW);
  },
});

export default Factory.create(FormControls, {
  defaultSelector: `.js-mainContainer form[data-controlled], .js-footer form[data-controlled]`,
});
